import type { Coding } from '@medplum/fhirtypes'
import type { ExtendedObservation } from '@seen/core/models/observation'
import {
  groupByAnswerSet,
  observationAnswerSetId,
} from '@seen/core/observationReferences'
import { cn } from '@seen/ui'
import { orderBy } from 'lodash-es'
import type { JSX } from 'react'
import { LastValueChip } from '#src/components/rich-text-editor/ObservationNode/LastValueChip.tsx'
import {
  lastValue,
  observationDate,
  shownValue,
} from '#src/components/rich-text-editor/ObservationNode/observationLastValue.ts'

const SYSTEM_LABELS = new Map([
  ['http://loinc.org', 'LOINC'],
  ['http://snomed.info/sct', 'SNOMED'],
])

interface ObservationHistoryProps {
  // Newest first.
  observations: ExtendedObservation[]
  // The node's own code. An observation recorded under another code in its
  // family is tagged with that code.
  coding: Coding
  className?: string
}

// Earlier observations of the same concept, one line each. Answers recorded
// together (one multi-select capture) share a line.
export function ObservationHistory({
  observations,
  coding,
  className,
}: ObservationHistoryProps): JSX.Element | null {
  const groups = groupByAnswerSet(observations, (observation) =>
    observationAnswerSetId(observation.identifiers())
  )
  if (!groups.length) {
    return null
  }
  return (
    <ul className={cn('flex flex-col gap-1.5 text-sm', className)}>
      {groups.map((group) => {
        const newest = orderBy(
          group.members,
          (member) => member.effectiveDateTime ?? '',
          'desc'
        )[0]
        if (!newest) {
          return null
        }
        const tag = otherCodeTag(newest, coding)
        const date = observationDate(newest)
        const value = orderBy(
          group.members,
          (member) => shownValue(member) ?? ''
        )
          .map((member) => shownValue(member))
          .filter((label) => !!label)
          .join(', ')
        return (
          <li
            key={group.groupId ?? newest.reference.reference}
            className="flex items-baseline gap-2"
          >
            <span className="min-w-0">{value}</span>
            {date && (
              <span className="shrink-0 text-foreground-secondary">{date}</span>
            )}
            {tag && (
              <span className="shrink-0 text-xs text-foreground-secondary">
                {tag}
              </span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

interface ObservationLastChipProps {
  // The most recent earlier observation, if there is one.
  observation: ExtendedObservation | undefined
  // Records that same answer again; when undefined there is no chip.
  // Readings (numbers, dates) have none: they are taken afresh, and their
  // history is under the node's History action.
  onRepeat: (() => void) | undefined
  disabled?: boolean
  // In place of the value, as free text shows its first line.
  shown?: string
}

// The most recent earlier answer after a field, as a muted chip that records
// it again in one click: "Continent · Oct 6".
export function ObservationLastChip({
  observation,
  onRepeat,
  disabled,
  shown,
}: ObservationLastChipProps): JSX.Element | null {
  const last = lastValue(observation)
  if (!last || !onRepeat) {
    return null
  }
  const text = shown ?? last.value
  return (
    <LastValueChip
      text={text}
      date={last.date}
      onClick={onRepeat}
      disabled={disabled}
      ariaLabel={
        shown === undefined
          ? `Use last value ${last.value}${last.date ? ` from ${last.date}` : ''}`
          : `Use last text${last.date ? ` from ${last.date}` : ''}`
      }
    />
  )
}

function otherCodeTag(
  observation: ExtendedObservation,
  coding: Coding
): string | null {
  const codings = observation.codeableConcept.coding ?? []
  if (
    codings.some(
      (candidate) =>
        candidate.system === coding.system && candidate.code === coding.code
    )
  ) {
    return null
  }
  const other = codings.find((candidate) => candidate.code)
  if (!other) {
    return null
  }
  const label = other.system ? SYSTEM_LABELS.get(other.system) : undefined
  return label ? `${label} ${other.code}` : (other.code ?? null)
}
